import React, { useEffect, useState } from 'react';
import { IioTable } from '../../components/IioTable';
import { useQueryClient } from '@tanstack/react-query';
import { useProject } from '../../hooks/useProject';
import { useTagCategories } from '../../hooks/useTagCategories';
import { useProjectTags, useDeleteTag } from '../../hooks/useProjectTags';
import { hasPermission } from '../../types/project';
import { navigateToTagExplorer } from '../../services/angular-bridge';
import { TagModal } from '../../components/modals/TagModal';
import type { Tag } from '../../hooks/useProjectTags';
import { PaginationControls } from '../../components/PaginationControls';
import { DeletingChip } from '../../components/DeletingChip';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { fetchingStyle } from '../../utilities/fetchingStyle';

interface Props {
  projectId: string;
}

export function TagsTab({ projectId }: Props) {
  const queryClient = useQueryClient();
  const { data: project } = useProject(projectId);
  const canManageTags = project ? hasPermission(project, 'MANAGE_TAGS') : false;

  const { data: allCategories } = useTagCategories();
  const categories = (allCategories ?? []).filter(c => c.name !== 'Batch');

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Select first category once loaded
  useEffect(() => {
    if (!selectedCategoryId && categories.length > 0) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  const { data: tagsPage, isPending, isFetching, refetch } = useProjectTags(projectId, selectedCategoryId, currentPage);
  const deleteTag = useDeleteTag(projectId);

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);
  const totalPages = tagsPage?.totalPages ?? 0;
  const totalTags = tagsPage?.totalTags ?? 0;

  function handleCategoryChange(id: string) {
    setSelectedCategoryId(id);
    setCurrentPage(0);
  }

  // Refetch first so the modal hydrates relationships saved since the page loaded.
  async function handleEdit(tag: Tag) {
    const { data } = await refetch();
    setEditingTag(data?.tags.find(t => t.id === tag.id) ?? tag);
  }
  function handleAdd() { setShowAddModal(true); }

  function handleDelete(tag: any) {
    const categoryNote = selectedCategory?.name === 'Batch' ? ' and all related batches ' : '';
    if (window.confirm(`Are you sure you want to permanently delete the tag "${tag.name}"${categoryNote}?`)) {
      deleteTag.mutate({ id: tag.id, name: tag.name });
    }
  }

  return (
    <>
      <h2>
        <i className="fas fa-tags" aria-hidden="true" />{' '}
        {project?.name} Tags
      </h2>

      <div className="clearfix">
        <div className="btn-group pull-left" style={{ marginBottom: 10 }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              type="button"
              className={`btn btn-default${selectedCategoryId === cat.id ? ' active' : ''}`}
              onClick={() => handleCategoryChange(cat.id)}
            >
              <i className="fas fa-tags" style={{ color: cat.defaultColor }} aria-hidden="true" />{' '}
              {cat.name}
            </button>
          ))}
        </div>

        {canManageTags && (
          <button
            className="btn btn-primary"
            style={{ marginLeft: 40 }}
            onClick={handleAdd}
          >
            Add Tag <i className="fas fa-plus-circle" aria-hidden="true" />
          </button>
        )}

        <p className="lead pull-right">
          {totalTags.toLocaleString()} {selectedCategory?.name} Tags
        </p>
      </div>

      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onGoTo={setCurrentPage}
        />
      )}

      {isPending ? (
        <LoadingPlaceholder label="Loading tags…" />
      ) : totalTags > 0 ? (
        <IioTable condensed style={fetchingStyle(isFetching)}>
          <colgroup>
            <col />
            {canManageTags && <col style={{ width: 120 }} />}
          </colgroup>
          <thead>
            <tr>
              <th>Name</th>
              {canManageTags && <th style={{ width: 130 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {(tagsPage?.tags ?? []).map(tag => (
              <tr key={tag.id}>
                <td>
                  <a onClick={() => navigateToTagExplorer(projectId, tag.id)}>
                    <i className="fas fa-circle" style={{ color: tag.color }} aria-hidden="true" />{' '}
                    {tag.name}
                  </a>
                </td>
                {canManageTags && (
                  <td>
                    {tag.status === 'Deleting' ? <DeletingChip /> : (
                      <>
                        <button
                          type="button"
                          className="btn btn-default btn-xs"
                          onClick={() => handleEdit(tag)}
                        >
                          <i className="fas fa-pencil-alt" aria-hidden="true" /> Edit
                        </button>{' '}
                        <button
                          type="button"
                          className="btn btn-xs btn-danger"
                          disabled={!tag.isNameEditable}
                          onClick={() => handleDelete(tag)}
                        >
                          <i className="fas fa-trash" aria-hidden="true" /> Delete
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </IioTable>
      ) : (
        <div className="alert alert-warning alert-block">
          <h4>No {selectedCategory?.name} tags found</h4>
        </div>
      )}

      {editingTag && (
        <TagModal projectId={projectId} tag={editingTag}
          onClose={() => setEditingTag(null)}
          onSaved={() => { setEditingTag(null); queryClient.invalidateQueries({ queryKey: ['projectTags', projectId] }); }}
        />
      )}
      {showAddModal && (
        <TagModal projectId={projectId} defaultCategoryId={selectedCategoryId ?? undefined}
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); queryClient.invalidateQueries({ queryKey: ['projectTags', projectId] }); }}
        />
      )}
    </>
  );
}
